import { useQuery } from "@apollo/client";
import {
  OnChangeFn,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import { Info, Search, X } from "lucide-react";
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  QUERY_CASE_TRACKER_CASES,
  QUERY_CASE_TRACKER_SUMMARY,
} from "api/queries/caseTracker";
import GenericError from "components/GenericError";
import PageContent from "components/Page/Content";
import { DataTable } from "components/ui/data-table";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { getCaseTrackerDetailUrl } from "routes/links";
import {
  CaseState,
  CaseTrackerCasesData,
  CaseTrackerCasesVariables,
  CaseTrackerSort,
  CaseTrackerSummaryData,
  CaseTrackerSummaryVariables,
} from "ts/interfaces/caseTracker";
import { cn } from "utils/cn";
import { HighlightTermsProvider } from "./Highlight";
import { getStage, Stage, STAGES } from "./stages";

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DEBOUNCE_MS = 300;

type UrlChanges = Record<string, string | null>;

const applyUrlChanges = (current: URLSearchParams, changes: UrlChanges) => {
  const next = new URLSearchParams(current);
  Object.entries(changes).forEach(([key, value]) =>
    value ? next.set(key, value) : next.delete(key),
  );
  return next;
};

const isSortable = (stage: Stage, id: string | null) =>
  stage.columns.some(
    (column) => column.id === id && column.enableSorting !== false,
  );

const formatCaseCount = (count: number) =>
  count > 999 ? "1k+" : count.toString();

const CaseTracker = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stage = getStage(searchParams.get("stage"));
  const query = searchParams.get("q") ?? "";
  const sortParam = searchParams.get("sort");
  const sorting: SortingState =
    sortParam && isSortable(stage, sortParam)
      ? [{ id: sortParam, desc: searchParams.get("dir") === "desc" }]
      : [];
  const [search, setSearch] = useState(query);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE_OPTIONS[1],
  });

  const resetPage = () =>
    setPagination((current) => ({ ...current, pageIndex: 0 }));

  const updateUrl = (changes: UrlChanges) =>
    setSearchParams((current) => applyUrlChanges(current, changes), {
      replace: true,
    });

  useEffect(() => {
    setSearch((current) => (current.trim() === query ? current : query));
  }, [query]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const trimmed = search.trim();
      if (trimmed === query) {
        return;
      }
      setSearchParams(
        (current) => applyUrlChanges(current, { q: trimmed || null }),
        { replace: true },
      );
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search, query, setSearchParams]);

  // Ghost pill animation
  const stageGroupRef = useRef<HTMLDivElement>(null);
  const stagePillRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const group = stageGroupRef.current;
    const pill = stagePillRef.current;
    if (!group || !pill) return;

    const positionPill = () => {
      const active = group.querySelector<HTMLElement>('[aria-pressed="true"]');
      if (!active) return;
      pill.style.width = `${active.offsetWidth}px`;
      pill.style.height = `${active.offsetHeight}px`;
      pill.style.transform = `translateX(${active.offsetLeft}px)`;
    };

    positionPill();

    // Button widths change with case counts and viewport size
    const observer = new ResizeObserver(positionPill);
    group
      .querySelectorAll("button")
      .forEach((button) => observer.observe(button));
    return () => observer.disconnect();
  }, [stage.key]);

  const [sort] = sorting;

  const { data, previousData, loading, error } = useQuery<
    CaseTrackerCasesData,
    CaseTrackerCasesVariables
  >(QUERY_CASE_TRACKER_CASES, {
    fetchPolicy: "no-cache",
    variables: {
      states: stage.states,
      search: query || undefined,
      sortBy: sort?.id as CaseTrackerSort | undefined,
      sortDirection: sort ? (sort.desc ? "DESC" : "ASC") : undefined,
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    },
  });

  const { data: latestSummary, previousData: previousSummary } = useQuery<
    CaseTrackerSummaryData,
    CaseTrackerSummaryVariables
  >(QUERY_CASE_TRACKER_SUMMARY, {
    fetchPolicy: "no-cache",
    variables: { search: query || undefined },
  });

  // The summary only reloads when the search changes, so previous counts
  // belong to a different search: the tabs keep them (faded) to avoid
  // jumping, but only current counts are treated as matches.
  const summaryLoading = !latestSummary;
  const countIn = (
    data: CaseTrackerSummaryData | undefined,
    states: CaseState[],
  ) =>
    data?.caseTracker.summary
      .filter((item) => states.includes(item.state))
      .reduce((sum, item) => sum + item.caseCount, 0) ?? 0;
  const caseCount = (states: CaseState[]) => countIn(latestSummary, states);
  const tabCount = (states: CaseState[]) =>
    countIn(latestSummary ?? previousSummary, states);

  // While a page or sort loads, keep showing the stage's previous rows. A
  // different stage's rows don't fit the new columns, so show skeletons.
  const loadedStageRef = useRef(stage.key);
  useEffect(() => {
    if (data) {
      loadedStageRef.current = stage.key;
    }
  }, [data, stage.key]);
  const fallbackData =
    loadedStageRef.current === stage.key ? previousData : undefined;
  const cases = (data ?? fallbackData)?.caseTracker.cases;
  const total = cases?.totalCount ?? caseCount(stage.states);
  const isFiltered = Boolean(query);

  // The summary already counts search matches per stage (it feeds the tab
  // counts), so pointing at other stages costs no extra request.
  const otherStageMatches = query
    ? STAGES.filter((item) => item.key !== stage.key)
        .map((item) => ({ stage: item, count: caseCount(item.states) }))
        .filter(({ count }) => count > 0)
    : [];

  const selectStage = (next: Stage) => {
    const keepSort = isSortable(next, sort?.id ?? null);
    updateUrl({
      stage: next.key,
      ...(keepSort ? {} : { sort: null, dir: null }),
    });
    resetPage();
  };

  // Saans contextual alternates turn "(1)" into a circled 1, hence calt off.
  const otherStageLinks = otherStageMatches.map(({ stage: item, count }, i) => (
    <Fragment key={item.key}>
      {i > 0}
      <button
        type="button"
        onClick={() => selectStage(item)}
        className="flex font-semibold text-primary underline underline-offset-4 [font-feature-settings:'calt'_0] hover:no-underline"
      >
        {item.label} ({formatCaseCount(count)})
      </button>
    </Fragment>
  ));

  const noResultsMessage = () =>
    query ? (
      <>
        No results matching &ldquo;{query}&rdquo; for{" "}
        {stage.label.toLowerCase()}.
      </>
    ) : (
      "No cases in this stage."
    );

  const clearSearch = () => {
    setSearch("");
    updateUrl({ q: null });
    resetPage();
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const [next] = typeof updater === "function" ? updater(sorting) : updater;
    updateUrl({
      sort: next?.id ?? null,
      dir: next ? (next.desc ? "desc" : "asc") : null,
    });
    resetPage();
  };

  return (
    <PageContent>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Case tracker</h1>
            <p className="mt-1 text-brand-copy-light">
              Every case you&apos;ve submitted, grouped by where it is in the
              journey.
            </p>
          </div>
        </div>

        <div
          ref={stageGroupRef}
          role="group"
          aria-label="Case stage"
          className="relative flex gap-1 overflow-x-auto rounded-xl border bg-background p-1.5 shadow-sm"
        >
          {/* Ghost pill that follows the active stage to animate its background */}
          <div
            ref={stagePillRef}
            className="absolute left-0 z-0 rounded-lg bg-primary transition-all ease-out"
          />
          {STAGES.map((item) => {
            const active = item.key === stage.key;
            return (
              <button
                key={item.key}
                type="button"
                aria-pressed={active}
                onClick={() => selectStage(item)}
                className={cn(
                  "z-10 flex flex-1 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active
                    ? " text-primary-foreground"
                    : "text-brand-copy-light hover:bg-muted hover:text-foreground",
                )}
              >
                {item.label}
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs font-semibold transition-opacity",
                    summaryLoading && "opacity-50",
                    active
                      ? "bg-brand-lime text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {formatCaseCount(tabCount(item.states))}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-96">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search by applicant, postcode or reference"
              aria-label="Search cases"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 bg-background pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden"
            />
            {search && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              {isFiltered ? "Matching" : "All"} {stage.label.toLowerCase()}{" "}
              cases
              <span className="rounded-full border bg-white px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                {total.toLocaleString()}
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <Label
                htmlFor="rows-per-page"
                className="font-normal text-muted-foreground"
              >
                Rows per page
              </Label>
              <Select
                value={String(pagination.pageSize)}
                onValueChange={(value) =>
                  setPagination({ pageIndex: 0, pageSize: Number(value) })
                }
              >
                <SelectTrigger
                  id="rows-per-page"
                  className="h-8 w-[4.5rem] bg-background"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error ? (
            <GenericError error={error} />
          ) : (
            <HighlightTermsProvider query={query}>
              <DataTable
                columns={stage.columns}
                data={cases?.results ?? []}
                rowCount={total}
                pagination={pagination}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={handleSortingChange}
                getRowHref={(row) => getCaseTrackerDetailUrl(row.uuid)}
                loading={loading}
                noResultsMessage={noResultsMessage()}
                footer={
                  otherStageLinks.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex flex-wrap gap-2">
                        <Info
                          className="mt-0.5 h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        More results in
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {otherStageLinks}
                      </div>
                      .
                    </div>
                  )
                }
              />
            </HighlightTermsProvider>
          )}
        </div>
      </div>
    </PageContent>
  );
};

export default CaseTracker;
